import React, {Component} from 'react';
import PropTypes from 'prop-types';
import './LocaleSwitcher.less';
import {Modal} from '@homeaway/react-modal';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import {LocalizationContext} from '@homeaway/react-localization-components';
import {generateRedirectionUrl} from '../../utils';
import LocaleMenuItemWrapper from '../LocaleMenuItemWrapper/LocaleMenuItemWrapper';

/**
 * LocaleSwitcher
 *
 * Your final component will likely be made up of smaller sub-components.
 * Best-practice emphasizes the grouping of javascript, less, and unit tests
 * within the same directory for each sub-component. This keeps code modular
 * and makes navigating/mantaining code easier.
 *
 * You can see the importing of a less file above. Webpack is configured to
 * bundle the resultant CSS into the final javascript file. Host applications
 * only need to import the component's javascript and at runtime, the component
 * adds the CSS to the page. This is only possible for brand-agnostic components.
 * Today baseline is well equiped to provide all brand variation allowing your
 * component to not need any brand-specific styling. Baseline has various helper
 * classes for the typical colors which also make this a reality.
 */

const FASTPATH_ACTION_LOCATION = 'locale selector';

class LocaleSwitcher extends Component {
    static propTypes = {
        /** Array of supported locale objects, for each object, includes locale string, country name, language name and link/basePath.
         *  Link is optional. If not provided, the redirection link will be auto generated.
         * */
        locales: PropTypes.arrayOf(
            PropTypes.shape({
                link: PropTypes.string,
                locale: PropTypes.string.isRequired,
                countryName: PropTypes.string.isRequired,
                languageName: PropTypes.string.isRequired,
                basePath: PropTypes.string
            })
        ),
        translations: PropTypes.shape({
            selectLocaleTitle: PropTypes.string
        })
    };

    static defaultProps = {
        locales: [
            {
                'link': 'https://www.vrbo.com',
                'locale': 'en_US',
                'countryName': 'United States',
                'languageName': 'English',
                'basePath': ''
            }
        ],
        translations: {
            selectLocaleTitle: 'Select language'
        }
    };

    static contextType = LocalizationContext;

    constructor(props) {
        super(props);

        this.state = {
            locales: [...props.locales]
        };

        this.onSelectedLocaleDisplayClicked = this.onSelectedLocaleDisplayClicked.bind(this);
        this.handleAllLocalesClose = this.handleAllLocalesClose.bind(this);
    }

    componentDidMount() {
        let locales = this.state.locales.map((locale) => {
            let link = generateRedirectionUrl({
                path: `${window.location.pathname}${window.location.search}`,
                basePath: locale.basePath,
                currentBasePath: this.context.basePath || '',
                link: locale.link
            });
            return {...locale, link};
        });

        this.setState({locales});
    }

    get currentLocale() {
        return this.context.locale ? this.context.locale.substr(0, 5) : 'en_US';
    }

    get countryCode() {
        return this.currentLocale.substr(3, 2).toLowerCase();
    }

    get languageCode() {
        return this.currentLocale.substr(0, 2);
    }

    onSelectedLocaleDisplayClicked() {
        this.setState({showAllLocales: true});
        window.edap.push((edapInstance) => {
            edapInstance.trigger('header.link.click', {
                actionlocation: 'header.flag'
            });

            edapInstance.trigger('generic.event', {
                eventcategory: 'locale switcher',
                eventaction: 'locale header clicked'
            });
            edapInstance.trigger('display_locale_selector_details.selected', {
                action_location: FASTPATH_ACTION_LOCATION
            });
        });
    }

    onLocalesModalOpen() {
        window.edap.push((edapInstance) => {
            edapInstance.trigger('display_locale_set.presented', {
                action_location: FASTPATH_ACTION_LOCATION
            });
        });
    }

    handleAllLocalesClose() {
        this.setState({showAllLocales: false});
        window.edap.push((edapInstance) => {
            edapInstance.trigger('interaction.selected', {
                action_location: FASTPATH_ACTION_LOCATION,
                event_action: 'dismissed',
            });
        });
    }

    render() {
        const {locales} = this.state;
        const {translations: {selectLocaleTitle}} = this.props;
        const selectedLocaleDisplay = (
            <span>
                <i className={`flag-${this.countryCode} flag-country`} aria-hidden="true"/>
                <span className="LocaleSwitcher__toggle-language-code">
                    {this.languageCode.toUpperCase()}
                </span>
            </span>
        );

        return (
            <div className="LocaleSwitcher">
                <a className="LocaleSwitcher__toggle visible-lg visible-xl-block" onClick={this.onSelectedLocaleDisplayClicked}>
                    {selectedLocaleDisplay}
                </a>
                <Modal
                    id="all-locales-modal"
                    isOpen={this.state.showAllLocales}
                    closeButton={true}
                    onClose={this.handleAllLocalesClose}
                    title={selectLocaleTitle}
                    onModalOpen={this.onLocalesModalOpen}
                >

                    <div className="all-locales-container">
                        {locales.map((localeObj) => {
                            return (
                                <div className="locale-item" key={localeObj.locale}>
                                    <LocaleMenuItemWrapper
                                        locale={localeObj.locale}
                                        countryName={localeObj.countryName}
                                        languageName={localeObj.languageName}
                                        basePath={localeObj.basePath}
                                        link={localeObj.link}
                                        isActive={this.currentLocale === localeObj.locale}
                                    />
                                </div>
                            );
                        })}
                    </div>

                </Modal>
                <a className="hidden-lg hidden-xl LocaleSwitcher--MobileWeb" href="#" onClick={this.onSelectedLocaleDisplayClicked}>
                    {selectedLocaleDisplay}
                    <SVGIcon className="text-muted" inline markup={CHEVRON_RIGHT__16} />
                </a>
            </div>
        );
    }
}

export default LocaleSwitcher;
